/**
 * Seed Icon Renderer - Seed node icon
 *
 * Renders a gear/seed icon used for representing seed nodes
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Draw white gear icon on canvas
 *
 * Internal helper function that draws a complex gear shape used
 * for seed nodes.
 *
 * @param ctx - Canvas rendering context
 * @param x - X coordinate for gear
 * @param y - Y coordinate for gear
 * @param width - Width of the gear
 * @param height - Height of the gear
 * @param gearColor - Color for the gear (defaults to white)
 */
const drawWhiteGear = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  gearColor: string = '#FFFFFF'
): void => {
  const scaleX = width / 25;
  const scaleY = height / 31;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scaleX, scaleY);

  // White gear path - complex shape
  ctx.fillStyle = gearColor;
  ctx.beginPath();

  // Start of the complex gear path
  ctx.moveTo(23.6289, 22.748);
  ctx.bezierCurveTo(22.3675, 22.4944, 21.0667, 22.4848, 19.8014, 22.7196);
  ctx.bezierCurveTo(18.5361, 22.9545, 17.3314, 23.4292, 16.2565, 24.1165);
  ctx.bezierCurveTo(15.6367, 24.5133, 15.0617, 24.972, 14.541, 25.4851);
  ctx.bezierCurveTo(14.4986, 25.5331, 14.4412, 25.5666, 14.3774, 25.5803);
  ctx.bezierCurveTo(14.3136, 25.594, 14.247, 25.5873, 14.1875, 25.5611);
  ctx.bezierCurveTo(14.1254, 25.5399, 14.0718, 25.5003, 14.0342, 25.448);
  ctx.bezierCurveTo(13.9966, 25.3957, 13.9769, 25.3333, 13.978, 25.2697);
  ctx.lineTo(13.978, 20.9233);
  ctx.bezierCurveTo(13.9791, 20.8666, 13.9954, 20.8111, 14.0252, 20.7622);
  ctx.bezierCurveTo(14.0551, 20.7134, 14.0974, 20.6728, 14.1482, 20.6445);
  ctx.bezierCurveTo(14.198, 20.6167, 14.2544, 20.6021, 14.3119, 20.6021);
  ctx.bezierCurveTo(14.3693, 20.6021, 14.4258, 20.6167, 14.4756, 20.6445);
  ctx.bezierCurveTo(15.113, 21.0376, 15.8542, 21.2444, 16.61, 21.2401);
  ctx.bezierCurveTo(17.2933, 21.2524, 17.9674, 21.086, 18.5612, 20.7585);
  ctx.bezierCurveTo(19.0083, 20.5094, 19.4003, 20.1776, 19.7149, 19.7819);
  ctx.bezierCurveTo(20.0294, 19.3862, 20.2603, 18.9344, 20.3945, 18.4523);
  ctx.bezierCurveTo(20.6553, 17.5, 20.5238, 16.4869, 20.0278, 15.6265);
  ctx.bezierCurveTo(19.9737, 15.5308, 19.946, 15.4233, 19.9475, 15.3142);
  ctx.bezierCurveTo(19.9489, 15.2051, 19.9794, 15.0983, 20.036, 15.004);
  ctx.bezierCurveTo(20.0927, 14.9097, 20.1735, 14.8311, 20.2707, 14.7759);
  ctx.bezierCurveTo(20.3679, 14.7207, 20.4782, 14.6907, 20.5909, 14.6888);
  ctx.bezierCurveTo(21.6328, 14.6888, 22.632, 14.2883, 23.3687, 13.5754);
  ctx.bezierCurveTo(24.1055, 12.8624, 24.5194, 11.8955, 24.5194, 10.8873);
  ctx.bezierCurveTo(24.5194, 9.87907, 24.1055, 8.91214, 23.3687, 8.19922);
  ctx.bezierCurveTo(22.632, 7.4863, 21.6328, 7.08579, 20.5909, 7.08579);
  ctx.bezierCurveTo(20.4782, 7.08391, 20.3679, 7.05392, 20.2707, 6.9987);
  ctx.bezierCurveTo(20.1735, 6.94349, 20.0927, 6.86493, 20.036, 6.77062);
  ctx.bezierCurveTo(19.9794, 6.67632, 19.9489, 6.56946, 19.9475, 6.4604);
  ctx.bezierCurveTo(19.946, 6.35133, 19.9737, 6.24376, 20.0278, 6.14808);
  ctx.bezierCurveTo(20.5064, 5.28332, 20.621, 4.27302, 20.3477, 3.32839);
  ctx.bezierCurveTo(20.0743, 2.38376, 19.4342, 1.57794, 18.5612, 1.07941);
  ctx.bezierCurveTo(17.6777, 0.575724, 16.6256, 0.42631, 15.6298, 0.663078);
  ctx.bezierCurveTo(14.634, 0.899847, 13.7733, 1.50403, 13.2316, 2.34658);
  ctx.bezierCurveTo(13.1712, 2.43815, 13.088, 2.51353, 12.9896, 2.56575);
  ctx.bezierCurveTo(12.8911, 2.61797, 12.7807, 2.64534, 12.6685, 2.64534);
  ctx.bezierCurveTo(12.5562, 2.64534, 12.4458, 2.61797, 12.3474, 2.56575);
  ctx.bezierCurveTo(12.249, 2.51353, 12.1657, 2.43815, 12.1054, 2.34658);
  ctx.bezierCurveTo(11.7072, 1.7068, 11.1231, 1.19469, 10.426, 0.874185);
  ctx.bezierCurveTo(9.72885, 0.553683, 8.94962, 0.438988, 8.18555, 0.54442);
  ctx.bezierCurveTo(7.42148, 0.649852, 6.70643, 0.970739, 6.12967, 1.46702);
  ctx.bezierCurveTo(5.55291, 1.9633, 5.14, 2.61299, 4.94248, 3.33497);
  ctx.bezierCurveTo(4.68169, 4.28729, 4.81314, 5.30039, 5.30914, 6.16075);
  ctx.bezierCurveTo(5.35596, 6.25093, 5.37984, 6.35063, 5.3788, 6.45155);
  ctx.bezierCurveTo(5.37775, 6.55247, 5.35181, 6.65168, 5.30313, 6.74094);
  ctx.bezierCurveTo(5.25445, 6.83019, 5.18444, 6.9069, 5.09893, 6.96468);
  ctx.bezierCurveTo(5.01342, 7.02246, 4.91489, 7.05964, 4.81153, 7.07312);
  ctx.bezierCurveTo(3.76963, 7.07312, 2.77041, 7.47363, 2.03368, 8.18655);
  ctx.bezierCurveTo(1.29695, 8.89947, 0.883057, 9.8664, 0.883057, 10.8746);
  ctx.bezierCurveTo(0.883057, 11.8828, 1.29695, 12.8498, 2.03368, 13.5627);
  ctx.bezierCurveTo(2.77041, 14.2756, 3.76963, 14.6761, 4.81153, 14.6761);
  ctx.bezierCurveTo(4.92423, 14.678, 5.03452, 14.708, 5.13172, 14.7632);
  ctx.bezierCurveTo(5.22893, 14.8184, 5.30976, 14.897, 5.36638, 14.9913);
  ctx.bezierCurveTo(5.42301, 15.0856, 5.45353, 15.1925, 5.45497, 15.3015);
  ctx.bezierCurveTo(5.45641, 15.4106, 5.42873, 15.5182, 5.37461, 15.6138);
  ctx.bezierCurveTo(4.87862, 16.4742, 4.74716, 17.4873, 5.00795, 18.4396);
  ctx.bezierCurveTo(5.25991, 19.4071, 5.89485, 20.24, 6.77576, 20.7585);
  ctx.bezierCurveTo(7.36692, 21.0949, 8.04116, 21.27, 8.72691, 21.2654);
  ctx.bezierCurveTo(9.48279, 21.2697, 10.2239, 21.0629, 10.8614, 20.6698);
  ctx.bezierCurveTo(10.9111, 20.642, 10.9676, 20.6274, 11.0251, 20.6274);
  ctx.bezierCurveTo(11.0825, 20.6274, 11.139, 20.642, 11.1887, 20.6698);
  ctx.bezierCurveTo(11.2395, 20.6982, 11.2819, 20.7387, 11.3117, 20.7876);
  ctx.bezierCurveTo(11.3415, 20.8364, 11.3578, 20.8919, 11.359, 20.9486);
  ctx.lineTo(11.359, 25.3077);
  ctx.bezierCurveTo(11.3581, 25.3709, 11.3377, 25.4325, 11.3004, 25.4845);
  ctx.bezierCurveTo(11.263, 25.5364, 11.2105, 25.5763, 11.1495, 25.5991);
  ctx.bezierCurveTo(11.0893, 25.6215, 11.0237, 25.6263, 10.9608, 25.6127);
  ctx.bezierCurveTo(10.8979, 25.5992, 10.8406, 25.568, 10.7959, 25.5231);
  ctx.bezierCurveTo(9.64291, 24.3575, 8.20331, 23.4939, 6.61169, 23.013);
  ctx.bezierCurveTo(5.02008, 22.5322, 3.32844, 22.4497, 1.69494, 22.7733);
  ctx.bezierCurveTo(1.52866, 22.8145, 1.38404, 22.9138, 1.289, 23.0521);
  ctx.bezierCurveTo(1.19436, 23.1948, 1.16144, 23.3677, 1.19733, 23.5336);
  ctx.bezierCurveTo(1.65799, 25.614, 2.84079, 27.479, 4.54898, 28.8184);
  ctx.bezierCurveTo(6.25717, 30.1579, 8.38778, 30.891, 10.5864, 30.8959);
  ctx.lineTo(14.7506, 30.8959);
  ctx.bezierCurveTo(16.9528, 30.8898, 19.0863, 30.153, 20.795, 28.8086);
  ctx.bezierCurveTo(22.5036, 27.4641, 23.6842, 25.5933, 24.1396, 23.5083);
  ctx.bezierCurveTo(24.1755, 23.3424, 24.1426, 23.1694, 24.0479, 23.0268);
  ctx.bezierCurveTo(23.9507, 22.885, 23.8006, 22.7851, 23.6289, 22.748);
  ctx.closePath();

  // Center hole (using even-odd fill rule for subtraction)
  ctx.moveTo(13.6506, 15.6899);
  ctx.bezierCurveTo(13.6506, 15.9419, 13.5471, 16.1836, 13.3629, 16.3619);
  ctx.bezierCurveTo(13.1788, 16.5401, 12.9289, 16.6402, 12.6685, 16.6402);
  ctx.bezierCurveTo(12.408, 16.6402, 12.1582, 16.5401, 11.974, 16.3619);
  ctx.bezierCurveTo(11.7898, 16.1836, 11.6864, 15.9419, 11.6864, 15.6899);
  ctx.lineTo(11.6864, 15.4364);
  ctx.bezierCurveTo(11.6868, 15.1989, 11.6184, 14.9661, 11.4887, 14.7644);
  ctx.bezierCurveTo(11.359, 14.5628, 11.1734, 14.4005, 10.953, 14.296);
  ctx.lineTo(10.4685, 14.0299);
  ctx.bezierCurveTo(10.2604, 13.904, 10.0199, 13.8373, 9.7745, 13.8373);
  ctx.bezierCurveTo(9.52907, 13.8373, 9.28859, 13.904, 9.08047, 14.0299);
  ctx.lineTo(8.91023, 14.1186);
  ctx.bezierCurveTo(8.68771, 14.2323, 8.4289, 14.2603, 8.18593, 14.1968);
  ctx.bezierCurveTo(7.94296, 14.1333, 7.73386, 13.9831, 7.60074, 13.7764);
  ctx.bezierCurveTo(7.48434, 13.5596, 7.45746, 13.3081, 7.52554, 13.0728);
  ctx.bezierCurveTo(7.59362, 12.8375, 7.75157, 12.6361, 7.9674, 12.5093);
  ctx.lineTo(8.13763, 12.4206);
  ctx.bezierCurveTo(8.35743, 12.2886, 8.53193, 12.0967, 8.63913, 11.8689);
  ctx.bezierCurveTo(8.74633, 11.6412, 8.78143, 11.3878, 8.74, 11.1407);
  ctx.bezierCurveTo(8.72751, 10.9678, 8.72751, 10.7942, 8.74, 10.6212);
  ctx.bezierCurveTo(8.75787, 10.3831, 8.7059, 10.145, 8.59007, 9.93429);
  ctx.bezierCurveTo(8.47423, 9.72356, 8.29924, 9.54877, 8.08526, 9.43005);
  ctx.lineTo(7.92812, 9.35402);
  ctx.bezierCurveTo(7.70985, 9.22939, 7.54983, 9.02793, 7.4815, 8.79177);
  ctx.bezierCurveTo(7.41316, 8.55562, 7.44181, 8.30304, 7.56146, 8.08685);
  ctx.bezierCurveTo(7.6925, 7.878, 7.90067, 7.72515, 8.14382, 7.65927);
  ctx.bezierCurveTo(8.38696, 7.59339, 8.64689, 7.61941, 8.87095, 7.73204);
  ctx.lineTo(9.04118, 7.83342);
  ctx.bezierCurveTo(9.2493, 7.95929, 9.48979, 8.02603, 9.73521, 8.02603);
  ctx.bezierCurveTo(9.98064, 8.02603, 10.2211, 7.95929, 10.4292, 7.83342);
  ctx.bezierCurveTo(10.5833, 7.73257, 10.7453, 7.64359, 10.9138, 7.56731);
  ctx.bezierCurveTo(11.1341, 7.46282, 11.3198, 7.30049, 11.4494, 7.09884);
  ctx.bezierCurveTo(11.5791, 6.8972, 11.6476, 6.66435, 11.6471, 6.42686);
  ctx.lineTo(11.6471, 6.12274);
  ctx.bezierCurveTo(11.6471, 5.99793, 11.6725, 5.87435, 11.7218, 5.75905);
  ctx.bezierCurveTo(11.7712, 5.64374, 11.8435, 5.53897, 11.9347, 5.45072);
  ctx.bezierCurveTo(12.0259, 5.36247, 12.1342, 5.29247, 12.2533, 5.24471);
  ctx.bezierCurveTo(12.3725, 5.19695, 12.5002, 5.17236, 12.6292, 5.17236);
  ctx.bezierCurveTo(12.7582, 5.17236, 12.8859, 5.19695, 13.005, 5.24471);
  ctx.bezierCurveTo(13.1242, 5.29247, 13.2325, 5.36247, 13.3237, 5.45072);
  ctx.bezierCurveTo(13.4148, 5.53897, 13.4872, 5.64374, 13.5365, 5.75905);
  ctx.bezierCurveTo(13.5859, 5.87435, 13.6113, 5.99793, 13.6113, 6.12274);
  ctx.lineTo(13.6113, 6.31282);
  ctx.bezierCurveTo(13.6108, 6.55031, 13.6793, 6.78315, 13.809, 6.9848);
  ctx.bezierCurveTo(13.9386, 7.18644, 14.1242, 7.34877, 14.3446, 7.45327);
  ctx.bezierCurveTo(14.5131, 7.52955, 14.6751, 7.61852, 14.8291, 7.71937);
  ctx.bezierCurveTo(15.0373, 7.84524, 15.2777, 7.91198, 15.5232, 7.91198);
  ctx.bezierCurveTo(15.7686, 7.91198, 16.0091, 7.84524, 16.2172, 7.71937);
  ctx.lineTo(16.3874, 7.618);
  ctx.bezierCurveTo(16.6115, 7.50536, 16.8714, 7.47935, 17.1146, 7.54523);
  ctx.bezierCurveTo(17.3577, 7.61111, 17.5659, 7.76395, 17.6969, 7.97281);
  ctx.bezierCurveTo(17.8166, 8.189, 17.8452, 8.44157, 17.7769, 8.67773);
  ctx.bezierCurveTo(17.7085, 8.91389, 17.5485, 9.11534, 17.3303, 9.23997);
  ctx.lineTo(17.16, 9.34135);
  ctx.bezierCurveTo(16.9474, 9.4783, 16.7808, 9.67242, 16.6808, 9.89982);
  ctx.bezierCurveTo(16.5807, 10.1272, 16.5516, 10.378, 16.5969, 10.6212);
  ctx.bezierCurveTo(16.6101, 10.7984, 16.6101, 10.9762, 16.5969, 11.1534);
  ctx.bezierCurveTo(16.5791, 11.3915, 16.631, 11.6296, 16.7469, 11.8403);
  ctx.bezierCurveTo(16.8627, 12.051, 17.0377, 12.2258, 17.2517, 12.3445);
  ctx.lineTo(17.4219, 12.4332);
  ctx.bezierCurveTo(17.6378, 12.56, 17.7957, 12.7615, 17.8638, 12.9968);
  ctx.bezierCurveTo(17.9319, 13.2321, 17.905, 13.4836, 17.7886, 13.7004);
  ctx.bezierCurveTo(17.6555, 13.9071, 17.4464, 14.0573, 17.2034, 14.1208);
  ctx.bezierCurveTo(16.9604, 14.1843, 16.7016, 14.1563, 16.4791, 14.0425);
  ctx.lineTo(16.3089, 13.9538);
  ctx.bezierCurveTo(16.1007, 13.828, 15.8603, 13.7612, 15.6148, 13.7612);
  ctx.bezierCurveTo(15.3694, 13.7612, 15.1289, 13.828, 14.9208, 13.9538);
  ctx.lineTo(14.4363, 14.2199);
  ctx.bezierCurveTo(14.2159, 14.3244, 14.0303, 14.4868, 13.9006, 14.6884);
  ctx.bezierCurveTo(13.771, 14.8901, 13.7025, 15.1229, 13.703, 15.3604);
  ctx.lineTo(13.6506, 15.6899);
  ctx.closePath();

  ctx.fill('evenodd'); // Use even-odd fill rule for proper center hole

  ctx.restore();
};

/**
 * Create seed icon data URL
 *
 * Generates a gear/seed icon with circular background for use as
 * a seed node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @param isHighlighted - Whether the icon should appear highlighted
 * @returns Data URL string of the rendered icon
 */
export const createSeedIconDataURL = (
  currentTheme: 'light' | 'dark',
  isHighlighted: boolean = false
): string => {
  const config = getIconConfig('seed');
  const colors = getThemeColors('seed', currentTheme, isHighlighted);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position with manual centering adjustment for gear
  const iconScale = (size * config.iconScaleRatio) / config.iconWidth;
  const globeX = centerX - (config.iconWidth * iconScale) / 2;
  const globeY = centerY - (config.iconHeight * iconScale) / 2;

  // Draw gear icon
  drawWhiteGear(
    ctx,
    globeX,
    globeY,
    config.iconWidth * iconScale,
    config.iconHeight * iconScale,
    colors.icon
  );

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
